/**
 * Draft_Check review (route `/check-drafts/:id`; Feature: ai-check-generation, task 23.5;
 * Requirements 9.3, 9.4, 9.5, 9.7; Design: §Components 11).
 *
 * A Draft_Check *is* a Check payload, so this page **reuses {@link CheckForm}** rather than growing a
 * parallel form. That reuse is the point rather than a convenience: the type-specific spec sub-forms,
 * the `FieldErrorText` wiring, and the submit-time projection are the same ones a directly-created
 * Check goes through, so a draft edited here is validated against the same rules — and the two
 * surfaces cannot drift apart.
 *
 * **Field-error placement (Req 9.5).** The draft routes root their error paths at `DRAFT_PATH_ROOT`
 * (`services/src/generation/draft.ts`), whose value is the string `check` — precisely the root
 * {@link CheckForm}'s `FormField` paths already use. So `check.logicalName` and
 * `check.spec.expectedStatusRange` reach their inputs by being handed through **untranslated**; there
 * is no path-rewriting layer here, deliberately, because a rewriting layer is a thing that can be
 * wrong. Anything the form declares no field for — the synthetic `generation.output` (the model
 * returned nothing schema-conforming, so there *is* no field to fix), a bare `check` (the patch body
 * was not an object), a deep spec path such as `check.spec.steps[0].assertions[0].jsonPath` — is
 * caught by `CheckForm`'s existing `unmappedFieldErrors` / `FormErrorPanel` complement and shown in
 * the form-level panel. Every returned error therefore lands somewhere, and the fallback is
 * structural rather than a list of special cases. `generation.output` additionally gets a plain-
 * language note above the form, because "fill this in yourself" is not obvious from the raw message.
 *
 * **Only accept creates a Check (Req 9.4).** Visible in the code as an absence: this module never
 * imports or calls `createCheck`, and the form's `onSubmit` runs {@link ApiClient.patchCheckDraft},
 * which writes the draft record and nothing else. `acceptCheckDraft` is the one call here that can
 * produce a Check, and it is reachable only from the accept button. Accept sends **no body** — it
 * accepts the *stored* draft — so the copy on that action tells the reviewer to save first.
 *
 * **Viewer (Req 9.7).** `useCanWrite` gates the accept and discard actions out of the tree entirely
 * and renders the form read-only, matching `CheckEditPage`: the values are theirs to see, the writes
 * are not. Display only — the API's role matrix is the enforcement point.
 *
 * ## Where the draft was produced (Feature: customer-managed-inference, task 18.2)
 *
 * customer-managed-inference **Requirements 11.5, 11.6, 17.9**; Design: §Components 18, 22.
 *
 * A draft may have been produced in Soroush's AWS account or in the customer's own, and Requirement
 * 11.6 wants a reviewer to see *which* — with the account, the region, and the Inference_Profile of the
 * attempt that produced it — **before** accepting it. {@link DraftInferenceContext} renders that beside
 * the Source_Document provenance the page already shows, as a labelled disclosure rather than four more
 * rows whose meaning depends on where they sit.
 *
 * The context is a **join the server performs**, not a stored field, so it can legitimately be absent:
 * a draft whose Generation_Request has aged out of the thirteen-month audit window has none, and neither
 * does one written before this feature existed. The absent case says so rather than filling in a
 * plausible account and region, because a confident wrong answer about where a customer's documentation
 * was processed is worse than no answer.
 *
 * **Accepting a draft is completely unchanged (Requirement 17.9), and that is a claim about this file
 * too.** The Check an acceptance creates carries no mode, no region, and no execution-context field of
 * any kind; it is scheduled and reconciled exactly as any other Check. Visible here as an absence: the
 * accept call is `client.acceptCheckDraft(draftId)` and nothing reads `draft.inference` on the way to
 * it, so the execution context cannot reach the created Check even by accident. That is Property 19's
 * page-side counterpart, and the page tests assert it by driving the same acceptance under both modes
 * and requiring the call sequences to be identical.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { CheckType, FieldError } from '@soroush/core';

import type {
  CheckDraftDto,
  CheckDto,
  DraftWarningDto,
  GetCheckDraftResponse,
  InferenceExecutionContextDto,
  SourceDocumentRefDto,
} from '../api/types';
import { CHECK_PAYLOAD_DEFAULTS } from '../api/types';
import { useApiClient } from '../api/use-api-client';
import { useConfig } from '../config-context';
import { useCanWrite } from '../auth/use-can-write';
import { CheckForm } from '../components/CheckForm';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ErrorState, LoadingState } from '../components/LoadStates';
import { formatEpochSeconds } from '../lib/format';
// One spelling of the mode names, shared with the Inference configuration page (task 17.2).
import { MODE_LABELS } from '../lib/inference-copy';
import { useResource } from '../lib/use-resource';
import { useWrite } from '../lib/use-write';

/**
 * The one error path the generation feature reports that is **not** rooted at `DRAFT_PATH_ROOT`
 * (`GENERATION_OUTPUT_ERROR_PATH` in `services/src/generation/generate.ts`). It describes the model
 * *response* rather than a field of a configuration, so no input can own it; it is named here only to
 * render the explanatory note, never to place an error.
 */
const GENERATION_OUTPUT_ERROR_PATH = 'generation.output';

/** The Check types the form's selector understands; anything else is not a type we can render. */
const CHECK_TYPES: readonly CheckType[] = ['uptime', 'api', 'browser'];

/* -------------------------------------------------------------------------- */
/* Untyped draft configuration → the typed shape CheckForm pre-fills from      */
/* -------------------------------------------------------------------------- */

/**
 * Project a draft's `configuration` — an untyped, possibly **incomplete** record — onto the
 * {@link CheckDto} shape {@link CheckForm} pre-fills from. This function is the whole untyped→typed
 * boundary of the page, so the casts are here and nowhere else.
 *
 * Three deliberate decisions about absent fields:
 *
 *  - **`type` falls back to `uptime`** when absent or unrecognized, because the selector must render
 *    *something* and `uptime` is what a create form starts on. A recognized-but-unsubmittable
 *    `browser` is passed through instead of being rewritten, so the form's own "not yet executable"
 *    guard still fires rather than being hidden by this projection.
 *  - **`enabled`, `failureThreshold`, `maxRetries`, and `latencySensitive` fall back to
 *    {@link CHECK_PAYLOAD_DEFAULTS}** — the same values the platform would apply anyway, taken from
 *    one named constant rather than re-guessed at this call site. Without this an empty configuration
 *    would render blank numeric inputs that the form always submits, so every save would be rejected
 *    on two optional fields the reviewer never asked about.
 *
 *    That constant *mirrors* `@soroush/core`'s `CHECK_DEFAULTS` rather than importing it, because a
 *    runtime import from core does not survive `vite build` — core is CommonJS and Rollup cannot
 *    resolve a named export through its `__exportStar` re-export. The copy is pinned to core by
 *    `api/types.check-defaults.test.ts`; see the constant's own doc comment for the full reasoning.
 *  - **`projectId`, `logicalName`, `intervalSeconds`, `locations`, and `spec` are *not* defaulted.**
 *    They are the draft schema's required fields and have no platform default, so an absent one
 *    renders empty and comes back as a per-field error from the server. The server stays the single
 *    validator; this page invents no required value.
 *
 * A correction-exhausted draft's `{}` configuration therefore renders as an empty-but-usable form —
 * which is exactly the intended repair route (services Req 5.5, 7.4).
 */
export function checkShapeFromDraftConfiguration(configuration: Record<string, unknown>): CheckDto {
  const rawType = configuration.type;
  return {
    // Server-managed on a real Check and absent from a draft; the form reads neither.
    checkId: '',
    accountId: '',
    ...configuration,
    projectId: configuration.projectId as string,
    logicalName: configuration.logicalName as string,
    type: CHECK_TYPES.includes(rawType as CheckType) ? (rawType as CheckType) : 'uptime',
    enabled: (configuration.enabled as boolean) ?? CHECK_PAYLOAD_DEFAULTS.enabled,
    intervalSeconds: configuration.intervalSeconds as number,
    locations: configuration.locations as CheckDto['locations'],
    failureThreshold: (configuration.failureThreshold as number) ?? CHECK_PAYLOAD_DEFAULTS.failureThreshold,
    maxRetries: (configuration.maxRetries as number) ?? CHECK_PAYLOAD_DEFAULTS.maxRetries,
    latencySensitive: (configuration.latencySensitive as boolean) ?? CHECK_PAYLOAD_DEFAULTS.latencySensitive,
    spec: configuration.spec,
  };
}

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

export function CheckDraftReviewPage(): JSX.Element {
  const client = useApiClient();
  // The deployment's run locations (Req 8.2), for the form's location picker. Read here rather than
  // in CheckForm so the form keeps taking no context dependency.
  const { runnerRegions } = useConfig();
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const { id } = useParams();
  const draftId = id ?? '';

  const loaded = useResource<GetCheckDraftResponse>(
    () => client.getCheckDraft(draftId),
    [client, draftId],
  );

  /**
   * The draft as last confirmed by the API: the loaded one until a save succeeds, then the one the
   * `PATCH` returned. Held here rather than re-fetched because the patch response *is* the complete
   * updated draft — including the schema projection's dropped fields and any secret substitution — so
   * re-seeding the form from it shows the reviewer what was actually stored.
   */
  const [saved, setSaved] = useState<CheckDraftDto | undefined>(undefined);
  /** Bumped on every successful save; remounts the form so it re-seeds from the stored draft. */
  const [savedRevision, setSavedRevision] = useState(0);

  const save = useWrite();
  const act = useWrite();
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  if (loaded.status === 'loading') {
    return (
      <section>
        <DraftsBackLink />
        <h1>Review generated check</h1>
        <LoadingState label="Loading draft check…" />
      </section>
    );
  }

  if (loaded.status === 'error') {
    return (
      <section>
        <DraftsBackLink />
        <h1>Review generated check</h1>
        <ErrorState
          summary="Could not load this draft check."
          message={loaded.message}
          isAuthError={loaded.isAuthError}
        />
      </section>
    );
  }

  const draft = saved ?? loaded.data.checkDraft;
  const editable = canWrite && draft.state === 'pending';

  /**
   * Req 9.5: the last thing the API said about this draft's fields. A failed write's `fieldErrors`
   * take precedence over the draft's stored `validationErrors` because they are newer; a *successful*
   * write clears both (`useWrite` resets, and the server rewrites `validationErrors` to empty on an
   * edit that validates), so a repaired draft shows a clean form.
   */
  const fieldErrors: readonly FieldError[] | undefined =
    save.state.fieldErrors ?? act.state.fieldErrors ?? draft.validationErrors;
  const hasGenerationOutputError =
    fieldErrors?.some((error) => error.path === GENERATION_OUTPUT_ERROR_PATH) === true;

  return (
    <section>
      <DraftsBackLink />
      <h1>Review generated check</h1>

      <DraftProvenance draft={draft} />
      {/* Where the producing attempt actually ran, beside the profile the provenance card already
          names (customer-managed-inference Requirements 11.5, 11.6). */}
      <DraftInferenceContext inference={draft.inference} />
      <DraftWarnings warnings={draft.warnings} />
      <DraftStateNotice draft={draft} />

      {!canWrite ? (
        <p role="note" className="muted" data-testid="read-only-notice">
          Your role has read-only access, so this draft cannot be edited, accepted, or discarded.
        </p>
      ) : null}

      {hasGenerationOutputError ? (
        <p role="note" className="muted" data-testid="generation-output-notice">
          The model returned nothing usable for this draft, so its configuration is empty. Fill the
          fields in below and save to have the draft re-validated; the error is listed above the form
          because it describes the model response, not a field you can correct.
        </p>
      ) : null}

      <CheckForm
        key={savedRevision}
        mode="draft"
        availableLocations={runnerRegions}
        initial={checkShapeFromDraftConfiguration(draft.configuration)}
        readOnly={!editable}
        submitting={save.state.submitting}
        fieldErrors={fieldErrors}
        errorMessage={save.state.errorMessage}
        /*
         * The only write the form can trigger. It patches the *draft*; it cannot create a Check.
         *
         * The whole payload is sent, not a diff: the route is a merge patch, so a complete body is an
         * idempotent statement of intent and an omitted field would silently keep its stored value.
         * The spread is what widens the typed `CreateCheckPayload` to `CheckDraftPatch`
         * (`Record<string, unknown>`) — the draft configuration is untyped by design. The API projects
         * the merged result onto the Draft_Check schema whitelist, so `latencySensitive` (which the
         * form renders but that schema does not declare) is dropped rather than rejected; it becomes
         * editable through the ordinary Check edit path once the draft is accepted.
         */
        onSubmit={(payload) =>
          void save.run(
            () => client.patchCheckDraft(draftId, { ...payload }),
            (response) => {
              setSaved(response.checkDraft);
              setSavedRevision((revision) => revision + 1);
            },
          )
        }
        onCancel={() => navigate('/check-drafts')}
      />

      {editable ? (
        <div className="form-actions" data-testid="draft-actions">
          <button
            type="button"
            disabled={act.state.submitting || save.state.submitting}
            onClick={() =>
              void act.run(
                () => client.acceptCheckDraft(draftId),
                (response) => navigate(`/checks/${encodeURIComponent(response.check.checkId)}`),
              )
            }
          >
            Accept and create check
          </button>
          <button
            type="button"
            className="danger"
            disabled={act.state.submitting || save.state.submitting}
            onClick={() => {
              act.reset();
              setConfirmingDiscard(true);
            }}
          >
            Discard draft
          </button>
          <p className="muted">
            Accepting creates the check from the <strong>saved</strong> draft, so save any edits
            first. Discarding leaves the draft readable until it expires; neither action can be
            undone.
          </p>
        </div>
      ) : null}

      {/* An accept failure has no form field to attach to (a 409 on an already-resolved draft, a 404
          on one that aged out), so it is reported here beside the actions that caused it. Its
          per-field errors, if any, went to the form above through `fieldErrors`. */}
      {act.state.errorMessage !== undefined && !confirmingDiscard ? (
        <div role="alert" className="error-panel" data-testid="draft-action-error">
          <p>{act.state.errorMessage}</p>
        </div>
      ) : null}

      {confirmingDiscard ? (
        <ConfirmDialog
          title="Discard draft check"
          message="Discarding marks this draft as discarded and no check is created from it. It stays readable until it expires."
          confirmLabel="Discard draft"
          busy={act.state.submitting}
          errorMessage={act.state.errorMessage}
          onConfirm={() =>
            void act.run(
              () => client.discardCheckDraft(draftId),
              () => {
                setConfirmingDiscard(false);
                navigate('/check-drafts');
              },
            )
          }
          onCancel={() => {
            act.reset();
            setConfirmingDiscard(false);
          }}
        />
      ) : null}
    </section>
  );
}

function DraftsBackLink(): JSX.Element {
  return (
    <p className="muted">
      <Link to="/check-drafts">← All draft checks</Link>
    </p>
  );
}

/**
 * Where the draft came from (Req 9.3): the Source_Document **reference** and the Inference_Profile
 * that produced it. A reference, never content — the `pasted` mode has no stored document at all, so
 * it can only report a character count.
 */
function DraftProvenance({ draft }: { draft: CheckDraftDto }): JSX.Element {
  return (
    <dl className="card" data-testid="draft-provenance">
      <dt>Source document</dt>
      <dd data-testid="draft-source-document">{sourceDocumentSummary(draft.sourceDocument)}</dd>

      <dt>Inference profile</dt>
      <dd data-testid="draft-inference-profile">{draft.inferenceProfileId}</dd>

      <dt>Correction attempts</dt>
      <dd>{draft.correctionAttempts}</dd>

      <dt>State</dt>
      <dd data-testid="draft-state">{draft.state}</dd>

      <dt>Created</dt>
      <dd>{formatEpochSeconds(draft.createdAtEpoch)}</dd>

      <dt>Expires</dt>
      <dd>{formatEpochSeconds(draft.expiresAtEpoch)}</dd>
    </dl>
  );
}

/** The id the execution-context disclosure is named by; one instance renders per page. */
const INFERENCE_CONTEXT_HEADING_ID = 'draft-inference-context-heading';

/**
 * Where the attempt that produced this draft executed: the mode, the AWS account, the region, and the
 * Inference_Profile (customer-managed-inference Requirements 11.5, 11.6).
 *
 * Rendered beside {@link DraftProvenance}'s Inference_Profile row rather than folded into it, and named
 * by its own heading, so the four facts are a labelled disclosure a reviewer can find and a screen
 * reader can announce as a unit — not four more `<dt>`/`<dd>` pairs whose significance depends on
 * position. The mode is stated in words from the shared {@link MODE_LABELS}; nothing here carries meaning
 * by colour.
 *
 * In `customer_hosted` mode {@link InferenceExecutionContextDto.accountId} is the account the
 * **assumed-role ARN STS returned** named, so it is the account the invocation truly ran in rather than
 * the one somebody typed into a role ARN (Requirement 11.3). It is not a secret — an account identifier
 * is not credential material, while a session token is (Requirement 11.7) — and no credential, External
 * Id, or role ARN is on this type to render.
 *
 * **The absent case is a real one and says so.** The context is joined off the originating
 * Generation_Request, so a draft whose request has aged out of the audit window, and any draft written
 * before this feature existed, has none. Reporting that plainly is the point: a reviewer deciding whether
 * to accept a draft is better served by "not recorded" than by a mode this page guessed.
 */
function DraftInferenceContext({
  inference,
}: {
  inference: InferenceExecutionContextDto | undefined;
}): JSX.Element {
  if (inference === undefined) {
    return (
      <p
        role="note"
        aria-label="Where this draft was produced"
        className="muted"
        data-testid="draft-inference-context-absent"
      >
        Where this draft was produced was not recorded, so the account and region that generated it
        cannot be shown. Drafts generated before execution context was recorded, and drafts whose
        generation request has aged out of the audit history, read this way.
      </p>
    );
  }
  return (
    <section
      aria-labelledby={INFERENCE_CONTEXT_HEADING_ID}
      className="card"
      data-testid="draft-inference-context"
    >
      <h2 id={INFERENCE_CONTEXT_HEADING_ID}>Where this draft was produced</h2>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Inference mode</dt>
          <dd data-testid="draft-inference-mode">{MODE_LABELS[inference.mode]}</dd>
        </div>
        <div className="detail-row">
          <dt>AWS account</dt>
          <dd data-testid="draft-inference-account">{inference.accountId}</dd>
        </div>
        <div className="detail-row">
          <dt>Region</dt>
          <dd data-testid="draft-inference-region">{inference.region}</dd>
        </div>
        <div className="detail-row">
          <dt>Inference profile</dt>
          <dd data-testid="draft-inference-context-profile">{inference.inferenceProfileId}</dd>
        </div>
      </dl>
    </section>
  );
}

/** One line describing a {@link SourceDocumentRefDto}, per mode. */
export function sourceDocumentSummary(reference: SourceDocumentRefDto): string {
  if (reference.mode === 'pasted') {
    return `Pasted text · ${reference.characterCount} characters`;
  }
  if (reference.mode === 'url') {
    // `documentKey` and the size are absent until the worker's fetch completes, and stay absent on a
    // fetch that was refused or failed.
    const stored =
      reference.documentKey === undefined
        ? 'not fetched'
        : `${reference.documentKey} · ${reference.byteLength ?? 0} bytes`;
    return `URL · ${reference.url} · ${stored}`;
  }
  return `Upload · ${reference.documentKey} · ${reference.contentType} · ${reference.byteLength} bytes`;
}

/**
 * The credential-substitution warnings (services Req 15.6): the API replaced a literal credential in
 * the generated configuration with a `${secret:…}` reference before persisting. Worth surfacing
 * prominently — the reviewer needs to know a secret must exist under that reference before the Check
 * can run, and that the literal the document contained was deliberately not kept.
 */
function DraftWarnings({ warnings }: { warnings?: DraftWarningDto[] }): JSX.Element | null {
  if (warnings === undefined || warnings.length === 0) return null;
  return (
    <div role="note" className="error-panel" data-testid="draft-warnings">
      <p>
        A literal credential in the source document was replaced with a secret reference before this
        draft was stored. Store the secret under that reference before the check runs.
      </p>
      <ul>
        {warnings.map((warning) => (
          <li key={`${warning.path}:${warning.secretRef}`}>
            {warning.path} → {warning.secretRef}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Why an already-resolved draft offers no actions. An accepted draft links to the Check acceptance
 * created (`createdCheckId`, present exactly because a repeated acceptance is idempotent), which is
 * where any further change belongs — the draft itself is finished.
 */
function DraftStateNotice({ draft }: { draft: CheckDraftDto }): JSX.Element | null {
  if (draft.state === 'pending') return null;
  return (
    <p role="note" className="muted" data-testid="draft-resolved-notice">
      {draft.state === 'accepted' ? (
        <>
          This draft was accepted, so it can no longer be edited.{' '}
          {draft.createdCheckId !== undefined ? (
            <Link to={`/checks/${encodeURIComponent(draft.createdCheckId)}`}>
              View the check it created
            </Link>
          ) : null}
        </>
      ) : (
        'This draft was discarded, so it can no longer be edited or accepted.'
      )}
    </p>
  );
}
